import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Price, { CURRENCY } from '../../components/Price';
import DateFormat from '../../components/DateFormat';
import Phone from '../../components/Phone';
import Button, { BUTTON_TYPE } from '../../components/Button';
import { PriceType } from '../../models/types';
import MinusIcon from '../../components/icons/Minus';
import AddIcon from '../../components/icons/Add';
import Info from '../../components/Info';
import Field from '../../components/Field';
import Badge, {BADGE_TYPE} from '../../components/Badge';

const TicketView = ({ ticket, schedule, place, onChange }) => {
  const {t} = useTranslation(['common', 'route']);

  const [priceRows, setPriceRows] = useState([]);
  const [leftToPay, setLeftToPay] = useState(0);
  const [totalSeats, setTotalSeats] = useState([]);

  const handleIncrement = (price) => {
    price.seat = Math.min(price.seat + 1, (ticket.passengers.find((p) => p.id === price.id) || {count: 0}).count);
    setPriceRows([...priceRows]);

    let currentLeftToPay = priceRows.reduce((amount, price) => amount + price.count * price.actualPrice.leftToPay, 0);
    setLeftToPay(Math.max(currentLeftToPay), 0);

    let seats = priceRows.reduce((seat, price) => {
      seat[price.id] = price.seat;
      return seat;
    }, {});

    onChange && onChange(ticket.seats[0].transport.id, seats);
  };

  const handleDecrement = (price) => {
    price.seat = price.seat - 1;
    setPriceRows([...priceRows]);

    let currentLeftToPay = priceRows.reduce((amount, price) => amount + price.count * price.actualPrice.leftToPay, 0);
    setLeftToPay(Math.max(currentLeftToPay), 0);

    let seats = priceRows.reduce((seat, price) => {
      seat[price.id] = price.seat;
      return seat;
    }, {});

    onChange && onChange(ticket.seats[0].transport.id, seats);
  };

  useEffect(() => {
    ticket.passengers.forEach((p) => p.seat = p.count);

    let totalSeats = ticket.seats.reduce((ts, transport) => ts.concat(transport.userSeats.reduce((s, seat) => { s.push({ transport: transport.transport.id, seat, price: '' }); return s; }, [])), []);

    setTotalSeats(totalSeats);
    setPriceRows(ticket.passengers);
    setLeftToPay(ticket.leftToPay);

    let seats = ticket.passengers.reduce((seat, price) => {
      seat[price.id] = Math.max(price.actualCount, price.seat);
      return seat;
    }, {});

    onChange && onChange(ticket.seats[0].transport.id, seats);
  }, []);

  return (
    <div>
      <Badge className="mb-3" type={BADGE_TYPE.WARN}>
          {ticket.comment}
      </Badge>
      <Info title={t('route:ticket_input.total_amount')}>
        <Price currency={CURRENCY.RUB} value={leftToPay} />
      </Info>
      <Field title={t('route:ticket_input.excursion')}>
        {ticket.excursion.title}
      </Field>
      <Field title={t('route:ticket_input.date_departure')}>
        <DateFormat.Day date={schedule.date} /> <DateFormat.Time date={place.time ? place.time : schedule.date} />
      </Field>
      <Field title={t('route:ticket_input.client')}>
        {ticket.user.profile.fullName} <Phone.Call value={ticket.user.phone} />
      </Field>
      {ticket.seller && (
        <Field title={t('route:ticket_input.seller')}>
          {ticket.seller.user.profile.fullName}  <Phone.Call value={ticket.seller.phone} />
        </Field>
      )}
      <Field title={t('route:ticket_input.seats')}>
        {totalSeats.map((s) => s.seat).join(', ')}
      </Field>
      <Field title={t('route:ticket_input.transport')}>
        {ticket.seats.map((s) => s.transport?.number + ': ' + s.transport?.title).join(', ')}
      </Field>
      {priceRows.map((price, i) => (
        <Field key={price.id}>

          <div className="price">
            <div
                className="price-title"
                title={price.price.priceType === PriceType.CHILD ? (price.noSeat ? t('order:price_type.no_seat') : t('order:price_type.with_seat')) : ''}>

                <div>
                    {price.price.title}
                    {price.description && (
                        <span> - {price.description}</span>
                    )}
                </div>

              {!price.price?.noSeat && price.count !== price.actualCount && (
                  <div className="description">{t('route:ticket_input.boarded')} <b>{price.actualCount}</b></div>
              )}
              {!price.price?.noSeat && price.seat !== price.actualCount && (
                <div className="description">{t(`route:ticket_input.${price.seat > price.actualCount ? 'new_board' : 'remove_board'}`)} <b>{price.seat - price.actualCount}</b></div>
              )}
              {(price.price?.noSeat || price.count !== price.seat) && (
                <div className="description">{t('route:ticket_input.count')} <b>{price.count}</b></div>
              )}
            </div>
            <span className="price-count flex-shrink-1">
              <Button className="price-button" variant={BUTTON_TYPE.SECONDARY} disabled={price.seat === 0} onClick={() => handleDecrement(price)}><MinusIcon /></Button>
            </span>
            <span className="price-count_content">{price.seat}</span>
            <span className="price-count flex-shrink-1">
              <Button className="price-button" variant={BUTTON_TYPE.SECONDARY} disabled={price.seat === price.count} onClick={() => handleIncrement(price)}><AddIcon /></Button>
            </span>
          </div>
        </Field>
      ))}
    </div>
  );
};

TicketView.propTypes = {
  ticket: PropTypes.any.isRequired,
  schedule: PropTypes.any.isRequired,
  place: PropTypes.any.isRequired,
  onChange: PropTypes.func,
};

export default TicketView;